import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Input, Button, Row, Col, message } from 'antd';
import { MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { track } from '../analytics';
import { requestTurnstileToken } from '../utils/turnstile';

const WhatsAppInterestModal = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const initialValues = useMemo(() => ({
    email: '',
    name: '',
    organizationName: '',
    phone: ''
  }), []);

  useEffect(() => {
    if (open) {
      form.setFieldsValue(initialValues);
    }
  }, [open, initialValues, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const turnstileToken = await requestTurnstileToken('quote_submit');
      const browserLanguage = navigator.language.split('-')[0];
      const payload = {
        intent: 'quote',
        email: values.email,
        organizationName: values.organizationName,
        area: 1,
        countries: 'Unknown',
        crops: 'Unknown',
        role: 'other',
        notes: `WhatsApp early access request. Name: ${values.name}. Phone: ${values.phone}.`,
        language: browserLanguage,
        calculator: null,
        turnstileToken,
      };

      await fetch(`${process.env.REACT_APP_API_URL}/request-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      message.success(t('whatsapp.success'));
      try { track('whatsapp_interest_submit_success'); } catch {}
      setLoading(false);
      onClose();
      form.resetFields();
    } catch (error) {
      message.error(t('whatsapp.error'));
      try { track('whatsapp_interest_submit_error'); } catch {}
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('whatsapp.title')}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={520}
    >
      <div className="py-2">
        <p className="text-gray-600 mb-6">{t('whatsapp.desc')}</p>
        <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false} initialValues={initialValues}>
          <Form.Item
            name="email"
            label={t('whatsapp.email')}
            rules={[
              { required: true, message: t('whatsapp.emailRequired') },
              { type: 'email', message: t('whatsapp.emailInvalid') }
            ]}
          >
            <Input size="large" placeholder={t('whatsapp.emailPlaceholder')} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label={t('whatsapp.name')}
                rules={[{ required: true, message: t('whatsapp.nameRequired') }]}
              >
                <Input size="large" placeholder={t('whatsapp.namePlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="phone"
                label={t('whatsapp.phone')}
                rules={[{ required: true, message: t('whatsapp.phoneRequired') }]}
              >
                <Input size="large" placeholder={t('whatsapp.phonePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="organizationName"
            label={t('whatsapp.org')}
            rules={[{ required: true, message: t('whatsapp.orgRequired') }]}
          >
            <Input size="large" placeholder={t('whatsapp.orgPlaceholder')} />
          </Form.Item>
          <Form.Item className="mb-0 mt-6">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              className="w-full bg-primary border-primary h-12 text-lg font-semibold"
              icon={<MessageCircle size={18} />}
              iconPosition="start"
            >
              {t('whatsapp.submit')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default WhatsAppInterestModal;
